import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "./stores/AuthStore";

interface IframeSyncHandlerProps {
  onAuthReady?: () => void;
  isBlocking?: boolean;
}

const IframeSyncHandler = ({ onAuthReady, isBlocking = false }: IframeSyncHandlerProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [token, setToken] = useState<string | null>(null);
  const [hasProcessedSync, setHasProcessedSync] = useState(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const { login, logout, isAuthenticated, checkAuth } = useAuthStore();
  
  // Only use navigation hooks when not in blocking mode (inside router context)
  let navigate: any = null;
  let location: any = { pathname: '/' };
  
  try {
    if (!isBlocking) {
      navigate = useNavigate();
      location = useLocation();
    }
  } catch (error) {
    // Router hooks not available, use defaults
    console.log("Router hooks not available, using defaults");
  }

  // Set the exact origin of the iframe app
  // Updated to new domain structure for iOS/Safari compatibility
  const IFRAME_ORIGIN = import.meta.env.VITE_CENTRAL_APP_URL || "https://beta.creativetoolbox.ai";
  // const IFRAME_ORIGIN = "http://localhost:5174"; // Use localhost for local development

  useEffect(() => {
    console.log('[IframeSyncHandler] useEffect triggered', {
      pathname: location?.pathname,
      isAuthenticated,
      hasProcessedSync,
      isAuthInitialized,
      isBlocking
    });

    // Check existing auth state first
    checkAuth();

    // If already authenticated, mark as initialized
    if (isAuthenticated) {
      console.log('[IframeSyncHandler] Already authenticated, marking as ready');
      setIsAuthInitialized(true);
      setHasProcessedSync(true);
      onAuthReady?.();
      return;
    }

    // Only handle sync if we're on the sync-session route or not authenticated
    const shouldHandleSync = location?.pathname === '/sync-session' || !isAuthenticated;

    console.log('[IframeSyncHandler] Should handle sync?', shouldHandleSync);

    if (!shouldHandleSync && hasProcessedSync) {
      console.log('[IframeSyncHandler] Already processed sync, marking as ready');
      setIsAuthInitialized(true);
      onAuthReady?.();
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      console.log('[IframeSyncHandler] Message received:', {
        origin: event.origin,
        expectedOrigin: IFRAME_ORIGIN,
        data: event.data
      });

      if (event.origin !== IFRAME_ORIGIN) {
        console.warn('[IframeSyncHandler] Message from unexpected origin, ignoring');
        return;
      }

      const { type, token, refreshToken } = event.data;

      if (type === "SESSION_SYNC") {
        if (token) {
          console.log("[IframeSyncHandler] ✅ Session token received from iframe");
          setToken(token);
          setHasProcessedSync(true);
          setIsAuthInitialized(true);

          // Store the token
          localStorage.setItem("smartai_auth_token", token);
          console.log('[IframeSyncHandler] Token stored in localStorage');

          login({accessToken: token, refreshToken: refreshToken});
          console.log('[IframeSyncHandler] Login function called');

          // Notify that auth is ready
          onAuthReady?.();

          // Navigate to agents dashboard after successful sync
          if (location?.pathname === '/sync-session' && navigate) {
            console.log("[IframeSyncHandler] Navigating to /agents after successful auth sync");
            navigate("/agents", { replace: true });
          }
        } else {
          console.error("[IframeSyncHandler] ❌ No token received in SESSION_SYNC");
          console.error("[IframeSyncHandler] This indicates the central auth app is not authenticated or failed to pass the token");
          console.error("[IframeSyncHandler] Possible causes:");
          console.error("  1. User is not logged in to the central app");
          console.error("  2. Central app session expired");
          console.error("  3. Central app's /sync-session page has a bug");
          console.error("  4. CORS or cookie issues preventing token access");

          setIsAuthInitialized(true);

          // Only redirect if we're specifically on the sync route
          if (location?.pathname === '/sync-session' && navigate) {
            console.log("[IframeSyncHandler] Redirecting to login - user needs to re-authenticate");
            // Don't call logout since there's nothing to clear
            navigate("/login", { replace: true });
          } else {
            onAuthReady?.();
          }
        }
      }
    };

    // Set a timeout to mark auth as initialized even if no message is received
    const authTimeout = setTimeout(() => {
      if (!isAuthInitialized) {
        console.warn("[IframeSyncHandler] ⏱️ Auth initialization timeout reached (5s)");
        setIsAuthInitialized(true);
        onAuthReady?.();
      }
    }, 5000); // 5 second timeout

    console.log('[IframeSyncHandler] Adding message listener and loading iframe');
    window.addEventListener("message", handleMessage);

    return () => {
      console.log('[IframeSyncHandler] Cleanup: removing message listener');
      window.removeEventListener("message", handleMessage);
      clearTimeout(authTimeout);
    };
  }, [location?.pathname, isAuthenticated, hasProcessedSync, login, logout, navigate, checkAuth, onAuthReady, isAuthInitialized]);

  // If this is a blocking instance and auth is not initialized, show loading
  if (isBlocking && !isAuthInitialized) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999
      }}>
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          borderRadius: '8px',
          backgroundColor: 'white',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 2s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p>Initializing authentication...</p>
          <iframe
            ref={iframeRef}
            src={`${IFRAME_ORIGIN}/sync-session`}
            title="Auth Sync"
            style={{ display: "none" }}
          />
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Only render iframe on sync-session route or when not authenticated
  if (location?.pathname !== '/sync-session' && isAuthenticated && hasProcessedSync) {
    return null;
  }

  if (import.meta.env.DEV) {
    console.log("IframeSyncHandler token:", token);
    console.log("Auth initialized:", isAuthInitialized);
  }

  return (
      <iframe
        ref={iframeRef}
        src={`${IFRAME_ORIGIN}/sync-session`}
        title="Embedded App"
        style={{ display: "none" }}
      />
  );
};

export default IframeSyncHandler;